import React, { useState } from 'react';
import { Typography, Box, Button, IconButton, Paper, CircularProgress, type Theme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import { Modal } from '../../components/modals/Modal';

export interface WarningModalContent {
  onContinue?: () => void | Promise<void>;
  onCancel?: () => void;
  title?: string;
  continueLabel?: string;
  cancelLabel?: string;
  word?: string;
  message?: string; // Custom message to override default message
  content?: string; // Custom description text to override default description
  /** Description body (e.g. "This action will be <b>deleted</b> immediately..."). When provided, overrides the default description. Can be string or ReactNode. */
  heading?: React.ReactNode;
  description?: React.ReactNode;
  type?: string;
}

interface WarningModalProps {
  open: boolean;
  content: WarningModalContent;
  loading: boolean;
  onClose: () => void;
  onContinue: () => void;
  /** 'unit' when from unit table, 'priceType' when from price type table */
  // type?: 'unit' | 'priceType';
}

/** Human-readable label for content.type (unit | priceType) */
// function getTypeLabel(type?: string): string {
//   if (type === 'priceType') return 'price type';
//   if (type === 'unit') return 'unit';
//   return 'item';
// }

export const WarningModal: React.FC<WarningModalProps> = ({ open, content, loading, onClose, onContinue }) => {
  // const typeLabel = getTypeLabel(content.type);

  return (
    <Modal
      open={open}
      onClose={onClose}
      sx={[
        (theme: Theme) => ({
          // Stack above other MUI modals (e.g. Add Item) which use theme.zIndex.modal (1300).
          zIndex: theme.zIndex.modal + 100,
        }),
        {
          '& > .MuiBox-root': {
            minWidth: '350px',
            maxWidth: '560px',
            maxHeight: '90vh',
          },
        },
      ]}
    >
      <Modal.Header>
        <Box
          sx={{
            backgroundColor: 'primary.main',
            padding: '5px 10px',
            display: 'flex',
            flexDirection: 'row',
            minWidth: '350px',
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: '#fff',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              {content.title}
              <IconButton onClick={onClose} disabled={loading} aria-label="close">
                <CloseIcon sx={{ color: '#fff' }} />
              </IconButton>
            </Typography>
          </Box>
        </Box>
      </Modal.Header>
      <Modal.Body>
        <Box
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'stretch',
            textAlign: 'left',
            borderRadius: 2,
            background: `
              linear-gradient(
                180deg,
                rgba(253, 83, 128, 0.06) 0%,
                rgba(255, 255, 255, 0.9) 60%
              )
            `,
          }}
        >
          {/* Main Question */}
            <>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  color: 'text.primary',
                  mb: 1.5,
                  fontSize: '20px',
                  letterSpacing: '-0.02em',
                  textAlign: 'center',
                }}
              >
                {content.heading}
              </Typography>

              {/* Description */}
              <Typography
                variant="body2"
                sx={{
                  color: 'text.secondary',
                  fontSize: '14px',
                  lineHeight: 1.6,
                  width: '100%',
                  textAlign: 'left',
                }}
              >
                {content.description}
              </Typography>
            </>
        </Box>
      </Modal.Body>

      <Modal.Footer>
        <Paper
          sx={{
            display: 'flex',
            justifyContent: 'end',
            padding: '5px',
            gap: 1,
          }}
        >
          <Button disabled={loading} onClick={onClose} variant="outlined">
            {content.cancelLabel}
          </Button>
          {loading ? (
            <LoadingButton sx={{ width: '108px' }} disabled={true} variant="contained">
              <CircularProgress color="inherit" size={16} />
            </LoadingButton>
          ) : (
            <Button disabled={loading} onClick={onContinue} variant="contained">
              {content.continueLabel}
            </Button>
          )}
        </Paper>
      </Modal.Footer>
    </Modal>
  );
};

interface UseWarningModalReturn {
  handleOpen: (content: WarningModalContent) => void;
  open: boolean;
  onClose: () => void;
  onContinue: () => void;
  content: WarningModalContent;
  loading: boolean;
}

export function  useWarningModal(): UseWarningModalReturn {
  const [open, setOpen] = useState(false);
  const [content, setContent] = useState<WarningModalContent>({
    onContinue: () => {},
    onCancel: () => {},
    title: 'Warning',
    continueLabel: 'Continue',
    cancelLabel: 'Close',
    word: 'Active',
  });
  const [loading, setLoading] = useState(false);

  const handleOpen = (newContent: WarningModalContent) => {
    setContent({
      title: 'Warning',
      continueLabel: 'Continue',
      cancelLabel: 'Close',
      ...newContent,
    });
    setOpen(true);
  };

  const handleClose = () => {
    try {
      setOpen(false);
      setLoading(true);
      if (typeof content?.onCancel === 'function') {
        content.onCancel();
      }
      setOpen(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  const handleContinue = async () => {
    try {
      setLoading(true);
      if (typeof content?.onContinue === 'function') {
        await content.onContinue();
      }
      setOpen(false);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  return {
    handleOpen,
    open,
    content,
    loading,
    onClose: handleClose,
    onContinue: handleContinue,
  };
}

export default useWarningModal;
